import { UploadFile } from 'features/ui/ui.slice';
import { apiRequest } from 'shared/middleware/api';
import { BrkfstFile, UploadOperation } from 'shared/typings/file';
import { formatAspectRatio } from 'shared/utilities/fileUtility';
import { getMediaMetaData } from 'shared/utilities/validator/utils';

interface FormatUploadOperationsParams {
  files: File[];
  submissionId?: number;
  collectionId?: number;
  validatorFields: {
    minAssetDuration: number;
    maxAssetDuration: number;
    moduleNum: number;
    fileType: string;
  };
  fileName?: string;
  fileNamesAlreadyInModule: Set<string>;
}

export const formatUploadOperations = async ({
  files,
  submissionId,
  collectionId,
  validatorFields,
  fileName,
  fileNamesAlreadyInModule,
}: FormatUploadOperationsParams): Promise<UploadOperation[]> => {
  // Phase 1: Resolve files and fetch metadata in parallel
  const fileEntries = await Promise.all(
    Array.from(files).map(async (file: any) => {
      let uploadingFile = file;
      if (uploadingFile.file) {
        // if the file's coming from fileExplorer, it's a list of files,
        // if it's coming directly from dragAndDrop, it's a list of objects as {file, mediaObjectURL}
        uploadingFile = uploadingFile.file;
      }
      const metadata = fileName ? await getMediaMetaData(uploadingFile) : null;
      return { uploadingFile, metadata };
    }),
  );

  // Phase 2: Assign names sequentially so deduplication works correctly
  return fileEntries.map(({ uploadingFile, metadata }) => {
    const uploadOperation: UploadOperation = {
      file: uploadingFile,
      metadata: {
        addWatermark: true,
        thumbnailOffset: 0.5,
        submissionValidatorFields: JSON.stringify(validatorFields),
      },
    };
    if (collectionId) uploadOperation.collectionId = collectionId;
    if (submissionId) uploadOperation.submissionId = submissionId;
    if (fileName && metadata) {
      const aspectRatio = formatAspectRatio(metadata);
      const fileExtension = uploadingFile.name.split('.').pop();
      uploadOperation.fileName = formatSubmissionFileName({
        baseName: fileName,
        aspectRatio,
        fileType: validatorFields.fileType,
        fileExtension,
        fileNamesAlreadyInModule,
      });
    }
    return uploadOperation;
  });
};

/**
 * Copies the files to the same collection using the submission file naming convention.
 * @param files
 * @param baseFileName
 * @param fileType
 */
export interface FileCopyParams {
  files: BrkfstFile[];
  baseFileName: string;
  fileType: string;
  fileNamesAlreadyInModule: Set<string>;
}
export async function copyFiles({
  files,
  baseFileName,
  fileType,
  fileNamesAlreadyInModule,
}: FileCopyParams): Promise<BrkfstFile[]> {
  const promises = files.map((file) =>
    apiRequest({
      path: `/file/${file.id}/copy`,
      method: 'POST',
      data: {
        name: formatSubmissionFileName({
          baseName: baseFileName,
          aspectRatio: formatAspectRatio(file.metadata),
          fileType,
          fileExtension: file.extension,
          fileNamesAlreadyInModule,
        }),
        sourceId: file.id,
      },
    }),
  );
  const response = await Promise.all(promises);
  return response.map((res) => res.data);
}

/**
 * Formats the Submission file names according to the naming convention where N differentiates files with the same base name: <BaseName>_<AspectRatio>_<FileType>(-N).<FileExtension>
 *
 * <BaseName> - <CreatorInitials><CreatorId>_B<briefId>_M<ModuleNumber>
 *
 * @param baseName
 * @param aspectRatio
 * @param fileType
 * @returns
 */
export function formatSubmissionFileName(params: {
  baseName: string;
  aspectRatio: string;
  fileType: string;
  fileExtension?: string;
  fileNamesAlreadyInModule: Set<string>;
}): string {
  const { baseName, aspectRatio, fileType, fileExtension = 'mp4', fileNamesAlreadyInModule } = params;

  const name = `${baseName}_${aspectRatio.replace(':', 'x')}_${fileType}.${fileExtension}`;

  if (fileNamesAlreadyInModule.has(name)) {
    // Extract the name without extension
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    const ext = name.substring(name.lastIndexOf('.'));

    let counter = 1;
    let newName = `${nameWithoutExt}-${counter}${ext}`;

    // Keep incrementing until we find a unique name
    while (fileNamesAlreadyInModule.has(newName)) {
      counter++;
      newName = `${nameWithoutExt}-${counter}${ext}`;
    }

    fileNamesAlreadyInModule.add(newName);
    return newName;
  } else {
    fileNamesAlreadyInModule.add(name);
    return name;
  }
}

export interface ValidateSelectedFilesParams {
  filesUploading: UploadFile[];
  filesSubmitted: Pick<BrkfstFile, 'id' | 'name' | 'metadata' | 'submissionValidatorFields'>[];
  filesCopied: BrkfstFile[];
}
